import { Placement } from '@popperjs/core';
import styled, { css } from 'styled-components';

import Chevron from '../../chevrons/Chevron';

export const StyledChevron = styled(Chevron)<{
  isOpened: boolean;
  disabled: boolean;
}>`
  position: absolute;
  right: ${({ isOpened }) => isOpened ? '8px' : '10px'};
  top: 50%;
  margin-top: -12px;  
`;

interface StyledDropdownButtonProps {
  isUnionControlGroup?: boolean;
  readOnly?: boolean;
  isValid?: boolean;
  hasWarning?: boolean;
  hasCaution?: boolean;
  isTouched?: boolean;
  value?: unknown;
  isOpened?: boolean;
  isEmpty?: boolean;
  placement?: Placement;
}

export const StyledDropdownButton = styled.button<StyledDropdownButtonProps>`
  position: relative;
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.gray13};  
  font-size: ${({ theme }) => theme.fontSize.base}px;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.white};
  appearance: none;
  outline: none;
  border-radius: ${({ theme }) => theme?.Input?.borderRadius};
  
  ${({ isUnionControlGroup }) => isUnionControlGroup && css`
    border-radius: 0;
  `}

  padding: 9px 34px 10px 9px;
  cursor: pointer;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: none;

  ${({ readOnly, disabled }) => !readOnly && !disabled && css<StyledDropdownButtonProps>`
    ${({ theme, isValid, isTouched }) => (!isValid && isTouched) && css`
      border: ${theme?.Input?.invalidBorder};
      padding: ${theme?.Dropdown?.invalidPadding};

      ${StyledChevron} {
        right: 9px;
      }
    `}

    ${({ theme, isValid, hasCaution, isTouched }) => (isValid && hasCaution && isTouched) && css`
      border: ${theme?.Input?.cautionBorder};
      padding: ${theme?.Dropdown?.invalidPadding};

      ${StyledChevron} {
        right: 9px;
      }
    `}

    ${({ theme, isValid, hasWarning, isTouched }) => (isValid && hasWarning && isTouched) && css`
      border: ${theme?.Input?.warningBorder};
      padding: ${theme?.Dropdown?.invalidPadding};

      ${StyledChevron} {
        right: 9px;
      }
    `}

    &:hover {
      ${({ theme, isValid, isTouched }) => (!isValid && isTouched) && css<StyledDropdownButtonProps>`
        border-color: ${theme?.Input?.invalidHoverBorderColor || theme?.Input?.invalidBorderColor || theme.colors.orange1};
      `}

      ${({ theme, isValid, hasWarning, hasCaution, isTouched, value }) => ((isValid && !hasWarning && !hasCaution) || !isTouched) && css<StyledDropdownButtonProps>`
        border-color: ${theme?.Input?.hoverBorderColor || theme.colors.focus};
        ${(value || value === 0) && isValid && css`
          border-bottom-color: ${theme?.Input?.validBottomBorderColor}
        `};
      `}

      ${({ theme, isValid, hasCaution, isTouched }) => isValid && hasCaution && isTouched && css<StyledDropdownButtonProps>`
        border-color: ${theme?.Input?.cautionHoverBorderColor || theme?.Input?.cautionBorderColor || theme.colors.caution};
      `}

      ${({ theme, isValid, hasWarning, isTouched }) => isValid && hasWarning && isTouched && css<StyledDropdownButtonProps>`
        border-color: ${theme?.Input?.warningHoverBorderColor || theme?.Input?.warningBorderColor || theme.colors.warning};
      `}
    }

    &:focus {
      box-shadow: ${({ theme }) => `${theme.colors.focus} 0 0 3px 1px`};
      border: ${({ theme }) => theme?.Input?.focusBorder};
      padding: ${({ theme }) => theme?.Dropdown?.focusPadding};

      ${StyledChevron} {
        right: 9px;
      }
    }

    &:focus:not(:focus-visible) {
      box-shadow: none
    }
  `}

  ${({ disabled, readOnly }) => disabled && !readOnly && css`
    background: ${({ theme }) => theme.colors.gray1};
    font-style: ${({ theme }) => theme?.Input?.disabledFontStyle};
    color: ${({ theme }) => theme?.Input?.disabledColor};
    cursor: default;
  `}

  ${({ readOnly }) => readOnly && css`
    background: ${({ theme }) => theme.colors.gray1};
    cursor: default;
  `}

  ${({ isOpened, placement }) => isOpened && placement === 'bottom' && css`
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  `}

  ${({ isOpened, placement }) => isOpened && placement === 'top' && css`
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  `}

  ${({ isOpened }) => isOpened && css`
    border-color: ${({ theme }) => theme.colors.focus};
    border: ${({ theme }) => theme?.Input?.focusBorder};
    padding: ${({ theme }) => theme?.Dropdown?.focusPadding};
  `}
`;

export const StyledDropdownText = styled.span``;

export const StyledEmptyDropdownText = styled.span<{
  disabled: boolean;
  readOnly: boolean;
}>`
  ${({ disabled, readOnly }) => !disabled && !readOnly && css`
    color: ${({ theme }) => theme.colors.shadowGray4};
  `}
`;
